import {
	FormItem,
	Toggle,
	ValidationError
} from "client/Components";
import { ReactElement } from "react";
import { LocationInput } from "./LocationInput";
import { IFormHandler } from "client/Forms";
import { CalendarAppointmentFormModel, RecurrencePatternKind } from "./common";
import { OnlineMeetingButton } from "./OnlineMeetingButton";
import { OnlineMeeting } from "client/Api/Notifications/notifications";
import styles from "./LocationSelectItem.module.less";
import { localize } from "client_legacy/ndaw/culture";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { TimeZone } from "client/DateAndTime/timezones";
import { isNil } from "client/Misc/utils";
import { RecurringAppointmentOperationKind } from "client/Api/Calendar/calendar";

export type LocationSelectItemProps = {
	attachable: IFormHandler<CalendarAppointmentFormModel>;
	meeting: OnlineMeeting | null;
	hasLabel?: boolean;
	timezone: TimeZone;
};

export const LocationSelectItem = (props: LocationSelectItemProps): ReactElement => {
	const {
		attachable,
		meeting,
		hasLabel = false,
		timezone
	} = props;

	const {
		id,
		startTime,
		endTime,
		isAllDay,
		recurrenceOptions,
		recurringOperationKind
	} = attachable
		.reducer
		.value;

	const shouldShowAvailability: boolean = isNil(id)
		? recurrenceOptions.recurrencePatternKind === RecurrencePatternKind.DontRepeat
		: isNil(recurringOperationKind)
			|| recurringOperationKind === RecurringAppointmentOperationKind.Current;

	return (
		<FormItem
			label={hasLabel
				? localize("_Location")
				: undefined
			}
			hasNoMargin={!hasLabel}
		>
			<div className={styles.root}>
				<div className={styles.field}>
					<LocationInput
						attachable={attachable.for("locations")}
						startTime={startTime}
						endTime={endTime}
						isAllDay={isAllDay}
						timeZone={timezone}
						shouldShowAvailability={shouldShowAvailability}
					/>
					<ValidationError {...attachable.for("locations").attachToValidationError()} />
				</div>
				<div className={styles.field}>
					{meeting
						? (
							<OnlineMeetingButton
								product={meeting.product}
								url={meeting.url}
							/>
						)
						: attachable.for("isTeamsMeeting")
							.whenType(
								x => x.isSet,
								handler => (
									<NoUniqueInputIdContext>
										<Toggle
											{...handler.for("value").attachToCheckbox()}
											text={localize("_TeamsMeeting")}
										/>
										<ValidationError {...handler.for("value").attachToValidationError()} />
									</NoUniqueInputIdContext>
								)
							)}
				</div>
			</div>
		</FormItem>
	);
};
LocationSelectItem.displayName = "LocationSelectItem";